/* Cart count sync — keeps the cart bubble AND drawer accurate in all scenarios:
 * 1. Page restored from bfcache (back/forward navigation) — most important
 * 2. After ATC on another tab (BroadcastChannel)
 * 3. When user clicks the cart icon (refresh drawer just in case)
 * 4. Initial page load
 *
 * The HTML uses class `m:hidden` to hide the counter; we also handle `hidden` for legacy compatibility.
 */
(function () {
  'use strict';

  function updateCartCountBubbles(itemCount) {
    var bubbles = document.querySelectorAll('.m-cart-count-bubble');
    if (!bubbles.length) return;
    bubbles.forEach(function (el) {
      if (itemCount > 0) {
        el.textContent = itemCount;
        el.classList.remove('m:hidden');
        el.classList.remove('hidden');
      } else {
        el.classList.add('m:hidden');
        el.classList.add('hidden');
      }
    });
    // Also update aria-label on the parent link if present
    document.querySelectorAll('.m-cart-icon-bubble').forEach(function (a) {
      a.setAttribute('aria-label', itemCount);
    });
    // Update empty state on cart drawer if present
    var drawer = document.querySelector('m-cart-drawer');
    if (drawer) {
      drawer.classList.toggle('m-cart--empty', itemCount === 0);
    }
  }

  // Refresh the actual content of the cart drawer (items, totals, etc) from the server.
  // Uses the theme's existing onCartDrawerUpdate() if available; otherwise falls back to
  // a manual fetch of the cart-drawer section.
  function refreshDrawerContent() {
    var drawer = document.querySelector('m-cart-drawer');
    if (!drawer) return Promise.resolve();
    if (typeof drawer.onCartDrawerUpdate === 'function') {
      try { drawer.onCartDrawerUpdate(true); } catch (e) {}
      return Promise.resolve();
    }
    // Fallback: manual fetch
    return fetch('/cart?section_id=cart-drawer', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!html) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        ['[data-minimog-cart-items]', '[data-minimog-cart-discounts]', '[data-cart-subtotal]', '[data-minimog-gift-wrapping]'].forEach(function (sel) {
          var src = parsed.querySelector(sel);
          var dst = document.querySelector(sel);
          if (src && dst) dst.innerHTML = src.innerHTML;
        });
      })
      .catch(function () {});
  }

  // Read current count shown in the DOM (if any)
  function getDomCartCount() {
    var bubble = document.querySelector('.m-cart-count-bubble');
    if (!bubble) return null;
    var hidden = bubble.classList.contains('m:hidden') || bubble.classList.contains('hidden');
    if (hidden) return 0;
    var n = parseInt(bubble.textContent, 10);
    return isNaN(n) ? null : n;
  }

  var inFlight = null;
  function refreshCartCount(force, alsoRefreshDrawer) {
    if (inFlight && !force) return inFlight;
    inFlight = fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (cart && typeof cart.item_count === 'number') {
          var current = getDomCartCount();
          if (current !== cart.item_count) {
            updateCartCountBubbles(cart.item_count);
            // Count changed — drawer content is stale too
            alsoRefreshDrawer = true;
          }
          if (alsoRefreshDrawer) {
            refreshDrawerContent();
          }
        }
        return cart;
      })
      .catch(function () { /* silent */ })
      .finally(function () { inFlight = null; });
    return inFlight;
  }

  // 1) bfcache restore — most important for the "go back from product page" scenario.
  // Skip if the inline early handler already registered it (avoids double fetch).
  if (!window.__bfcacheCartHandler) {
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) {
        // bfcache restore: ALWAYS refresh drawer content too, since the cached drawer may be stale
        refreshCartCount(true, true);
      }
    });
  } else {
    // The inline handler updates the count; we also need to refresh drawer content on bfcache
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) {
        // Small delay so the inline handler's count update happens first
        setTimeout(refreshDrawerContent, 50);
      }
    });
  }

  // 2) Cross-tab sync — when ATC happens on another tab, update this one too
  if ('BroadcastChannel' in window) {
    try {
      var channel = new BroadcastChannel('cart_updates');
      channel.addEventListener('message', function (e) {
        if (e.data && typeof e.data.item_count === 'number') {
          updateCartCountBubbles(e.data.item_count);
          // Also refresh drawer content since items changed
          refreshDrawerContent();
        }
      });
      window.__cartBroadcast = function (cart) {
        try { channel.postMessage({ item_count: cart.item_count, ts: Date.now() }); } catch (e) {}
      };
    } catch (e) {}
  }

  // 3) When tab becomes visible again (covers some edge cases bfcache doesn't)
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') {
      refreshCartCount();
    }
  });

  // 4) When user clicks the cart icon — refresh drawer content BEFORE it opens.
  // This catches any case where the drawer is stale (bfcache, cross-tab, third-party app, etc).
  // Use capture phase to run before the theme's own click handler that opens the drawer.
  document.addEventListener('click', function (e) {
    var icon = e.target && e.target.closest && e.target.closest('.m-cart-icon-bubble');
    if (!icon) return;
    // Don't block the click — just trigger an async refresh in parallel.
    // The drawer will open immediately (good UX); content will update within ~100-300ms.
    refreshDrawerContent();
    refreshCartCount();
  }, true);

  // 5) Hook into the theme's existing cart events to broadcast updates
  function hookEvents() {
    if (!window.MinimogEvents || !window.MinimogTheme || !window.MinimogTheme.pubSubEvents) {
      return setTimeout(hookEvents, 100);
    }
    var events = window.MinimogTheme.pubSubEvents;
    if (events.cartUpdate) {
      window.MinimogEvents.subscribe(events.cartUpdate, function (cart) {
        if (cart && typeof cart.item_count === 'number') {
          updateCartCountBubbles(cart.item_count);
          if (window.__cartBroadcast) window.__cartBroadcast(cart);
        } else {
          refreshCartCount(true, true).then(function (c) {
            if (c && window.__cartBroadcast) window.__cartBroadcast(c);
          });
        }
      });
    }
  }
  hookEvents();

  // Expose helpers for debugging / external use
  window.__refreshCartCount = refreshCartCount;
  window.__refreshDrawerContent = refreshDrawerContent;
})();

